"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import ContainedImage from "./ContainedImage";
import type { HomepageAsset } from "@/lib/homepage";

type ImageCarouselProps = {
  images: HomepageAsset[];
  visibleCount?: number;
  autoPlayInterval?: number;
  fit?: "cover" | "contain";
  frameClassName?: string;
  className?: string;
};

/**
 * Slide width per step, as a percentage of the track's own width. The track is
 * a block-level flex container, so its width equals the viewport wrapper's —
 * which makes these percentages breakpoint-correct without any measuring.
 */
const SLIDE_MS = 700;

const LAYOUT: Record<number, { item: string; step: string }> = {
  1: { item: "w-full", step: "[--step:100%]" },
  2: { item: "w-full md:w-1/2", step: "[--step:100%] md:[--step:50%]" },
  3: { item: "w-full md:w-1/3", step: "[--step:100%] md:[--step:33.3333%]" },
  4: { item: "w-full md:w-1/2 lg:w-1/4", step: "[--step:100%] md:[--step:50%] lg:[--step:25%]" },
};

export default function ImageCarousel({
  images,
  visibleCount = 3,
  autoPlayInterval = 3500,
  fit = "cover",
  frameClassName = "aspect-[4/3]",
  className = "",
}: ImageCarouselProps) {
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(true);
  const [paused, setPaused] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const restoreFrame = useRef<number | null>(null);

  const safeImages = useMemo(() => images.filter((image) => image.url), [images]);
  const count = safeImages.length;
  const layout = LAYOUT[visibleCount] ?? LAYOUT[3];
  const canMove = count > 1;

  /**
   * Clone `visibleCount` frames onto each end so the track can run past either
   * edge and be snapped back invisibly. Indices wrap, so this holds even when
   * there are exactly as many images as visible slots — the case that left the
   * activity carousel frozen before.
   */
  const track = useMemo(() => {
    if (count === 0) return [];
    const at = (i: number) => safeImages[((i % count) + count) % count];
    return [
      ...Array.from({ length: visibleCount }, (_, i) => at(count - visibleCount + i)),
      ...safeImages,
      ...Array.from({ length: visibleCount }, (_, i) => at(i)),
    ];
  }, [safeImages, count, visibleCount]);

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(query.matches);
    sync();
    query.addEventListener("change", sync);
    return () => query.removeEventListener("change", sync);
  }, []);

  // A backgrounded tab throttles timers but stops firing transitionend, so
  // letting autoplay run while hidden would advance the index without ever
  // snapping it back — the track ends up parked past its last frame.
  useEffect(() => {
    const sync = () => setHidden(document.visibilityState === "hidden");
    sync();
    document.addEventListener("visibilitychange", sync);
    return () => document.removeEventListener("visibilitychange", sync);
  }, []);

  useEffect(() => {
    if (paused || hidden || reduceMotion || !canMove) return;
    const timer = window.setInterval(() => setIndex((current) => current + 1), autoPlayInterval);
    return () => window.clearInterval(timer);
  }, [autoPlayInterval, paused, hidden, reduceMotion, canMove]);

  // Re-arm the transition on the frame after a silent snap, so the jump itself
  // never animates but the next step does.
  useEffect(() => {
    if (animate) return;
    restoreFrame.current = requestAnimationFrame(() => {
      restoreFrame.current = requestAnimationFrame(() => setAnimate(true));
    });
    return () => {
      if (restoreFrame.current !== null) cancelAnimationFrame(restoreFrame.current);
    };
  }, [animate]);

  // Full modulo, not a single -/+ count: an index can overshoot by more than one
  // step if several transitions were missed, and a partial fix would strand it.
  const settle = useCallback(() => {
    if (count === 0 || (index >= 0 && index < count)) return;
    setAnimate(false);
    setIndex(((index % count) + count) % count);
  }, [index, count]);

  // transitionend is the fast path, but it is not guaranteed — a backgrounded
  // tab or an interrupted transition can swallow it. settle() is idempotent, so
  // a timed backstop is safe to run alongside it.
  useEffect(() => {
    if (index >= 0 && index < count) return;
    const timer = window.setTimeout(settle, SLIDE_MS + 80);
    return () => window.clearTimeout(timer);
  }, [index, count, settle]);

  if (count === 0) return null;

  const offset = index + visibleCount;

  return (
    <div
      className={`relative ${className}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <button
        type="button"
        aria-label="Previous image"
        onClick={() => setIndex((current) => current - 1)}
        disabled={!canMove}
        className="absolute left-2 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border-4 border-black bg-white text-black shadow-md transition hover:scale-105 disabled:opacity-40"
      >
        <ChevronLeft size={34} strokeWidth={4} />
      </button>

      {/* px-14 keeps the frames clear of the arrows; the -mx-2 cancels the
          per-item padding at the two outer edges so the row still lines up. */}
      <div className="px-14">
        <div className="-mx-2 overflow-hidden">
          <div
            className={`flex ${layout.step}`}
            style={{
              transform: `translateX(calc(var(--step) * -${offset}))`,
              transition: animate && !reduceMotion ? `transform ${SLIDE_MS}ms cubic-bezier(0.4, 0, 0.2, 1)` : "none",
            }}
            onTransitionEnd={(event) => {
              if (event.target === event.currentTarget) settle();
            }}
          >
            {track.map((image, position) => (
              <div
                key={`${image.url}-${position}`}
                className={`shrink-0 px-2 ${layout.item}`}
                aria-hidden={position < visibleCount || position >= visibleCount + count}
              >
                {/* eager: the clone frames sit outside the viewport until the
                    track slides them in, so lazy loading leaves them blank at
                    exactly the moment they become visible. */}
                <ContainedImage
                  src={image.url}
                  alt={image.alt}
                  fit={fit}
                  eager
                  sizes={`(max-width: 768px) 100vw, ${Math.round(100 / visibleCount)}vw`}
                  className={`${frameClassName} bg-white`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <button
        type="button"
        aria-label="Next image"
        onClick={() => setIndex((current) => current + 1)}
        disabled={!canMove}
        className="absolute right-2 top-1/2 z-10 grid h-12 w-12 -translate-y-1/2 place-items-center rounded-full border-4 border-black bg-white text-black shadow-md transition hover:scale-105 disabled:opacity-40"
      >
        <ChevronRight size={34} strokeWidth={4} />
      </button>
    </div>
  );
}
